/**
 * Restaurant recipe scaling SSOT — POS sale + kitchen production share one math.
 *
 * Recipe.portions = BOM base units the formula is written for
 *   (menu plates for dishes, OR stock units of finished goods for prep recipes).
 *
 * Explosion always needs: explodeQuantity such that
 *   lineScale = explodeQuantity / recipe.portions
 * so explodeQuantity === recipe.portions ⇒ materials as written (1 full formula).
 *
 * Use cases:
 * - POS: explodeQuantity = sold plate count
 * - Production ×N batches: explodeQuantity = N * recipe.portions
 * - Production by FG target: batches = targetQty / outputPerBatch,
 *   explodeQuantity = batches * recipe.portions
 *   (outputPerBatch defaults to recipe.portions when BOM base === expected PF yield)
 */

import { normalizeRecipe } from './recipeModel';

/** How production scales multi-ingredient BOM (also re-exported by production/). */
export const PRODUCTION_SCALE_MODE = Object.freeze({
  /** Run the full recipe formula N times (ingredients × N). */
  BATCHES: 'batches',
  /**
   * Scale ingredients so formula output maps to planned FG qty
   * (batches equiv = plannedOutput / formulaOutputPerBatch).
   */
  OUTPUT: 'output'
});

/**
 * @param {unknown} mode
 * @returns {'batches'|'output'}
 */
export function normalizeProductionScaleMode(mode) {
  const key = String(mode || '')
    .trim()
    .toLowerCase();
  if (key === PRODUCTION_SCALE_MODE.OUTPUT) return PRODUCTION_SCALE_MODE.OUTPUT;
  return PRODUCTION_SCALE_MODE.BATCHES;
}

/**
 * @param {object|null|undefined} recipe
 * @returns {number}
 */
export function getRecipeBatchBase(recipe) {
  const n = Number(recipe?.portions);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

/**
 * Expected finished/stock units produced by one full formula run.
 * Defaults to BOM base (recipe.portions) so prep recipes stay 1:1 with the written yield.
 *
 * @param {object|null|undefined} recipe
 * @param {number} [override]
 */
export function getFormulaOutputPerBatch(recipe, override) {
  const o = Number(override);
  if (Number.isFinite(o) && o > 0) return o;
  const fromRecipe = Number(
    recipe?.outputQuantity ?? recipe?.formulaOutputQty ?? recipe?.outputQty
  );
  if (Number.isFinite(fromRecipe) && fromRecipe > 0) return fromRecipe;
  return getRecipeBatchBase(recipe);
}

/**
 * POS / dish sale: sold menu units → explode quantity.
 * @param {number} soldQty
 */
export function scaleForPosSale(soldQty) {
  const n = Number(soldQty);
  if (!Number.isFinite(n) || n <= 0) {
    return { success: false, error: 'soldQty must be > 0', code: 'invalid_sale_qty' };
  }
  return {
    success: true,
    context: 'pos_sale',
    explodeQuantity: n,
    soldQty: n
  };
}

/**
 * Production material scale — multi-ingredient BOM × batches or FG output target.
 *
 * @param {object} rawRecipe
 * @param {{
 *   scaleMode?: string,
 *   plannedBatches?: number,
 *   plannedOutputQty?: number,
 *   formulaOutputPerBatch?: number,
 *   plannedPortions?: number
 * }} [opts]
 */
export function scaleForProduction(rawRecipe, opts = {}) {
  const recipe = normalizeRecipe(rawRecipe) || rawRecipe;
  const batchBase = getRecipeBatchBase(recipe);
  const scaleMode = normalizeProductionScaleMode(opts.scaleMode);
  const formulaOutputPerBatch = getFormulaOutputPerBatch(recipe, opts.formulaOutputPerBatch);

  if (scaleMode === PRODUCTION_SCALE_MODE.OUTPUT) {
    const out = Number(opts.plannedOutputQty);
    if (!Number.isFinite(out) || out <= 0) {
      return {
        success: false,
        context: 'production',
        scaleMode,
        error: 'plannedOutputQty must be > 0 for output scale mode',
        code: 'invalid_output_qty'
      };
    }
    const plannedBatches = out / formulaOutputPerBatch;
    if (!(plannedBatches > 0)) {
      return {
        success: false,
        context: 'production',
        scaleMode,
        error: 'Invalid formulaOutputPerBatch',
        code: 'invalid_formula_output'
      };
    }
    return {
      success: true,
      context: 'production',
      scaleMode,
      batchBase,
      formulaOutputPerBatch,
      plannedBatches,
      explodeQuantity: plannedBatches * batchBase,
      plannedOutputQty: out
    };
  }

  let plannedBatches = Number(opts.plannedBatches);
  if (!Number.isFinite(plannedBatches) || plannedBatches <= 0) {
    const legacyExplode = Number(opts.plannedPortions);
    if (Number.isFinite(legacyExplode) && legacyExplode > 0) {
      plannedBatches = legacyExplode / batchBase;
    }
  }
  if (!Number.isFinite(plannedBatches) || plannedBatches <= 0) {
    return {
      success: false,
      context: 'production',
      scaleMode,
      error: 'plannedBatches must be > 0',
      code: 'invalid_batches'
    };
  }

  const defaultOutput = plannedBatches * formulaOutputPerBatch;
  const plannedOutputQty = Number(opts.plannedOutputQty);
  const output =
    Number.isFinite(plannedOutputQty) && plannedOutputQty > 0
      ? plannedOutputQty
      : defaultOutput;

  return {
    success: true,
    context: 'production',
    scaleMode,
    batchBase,
    formulaOutputPerBatch,
    plannedBatches,
    explodeQuantity: plannedBatches * batchBase,
    plannedOutputQty: output
  };
}

/**
 * Unified entry for explode callers.
 * @param {object} recipe
 * @param {{
 *   context: 'pos_sale'|'production',
 *   soldQty?: number,
 *   scaleMode?: string,
 *   plannedBatches?: number,
 *   plannedOutputQty?: number,
 *   formulaOutputPerBatch?: number,
 *   plannedPortions?: number
 * }} opts
 */
export function resolveRecipeExplodeQuantity(recipe, opts = {}) {
  if (opts.context === 'production') {
    return scaleForProduction(recipe, opts);
  }
  return scaleForPosSale(opts.soldQty ?? opts.plannedPortions ?? 1);
}
